import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/api";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Pause, Play, Copy, Edit2, Trash2, Megaphone, AlertTriangle, Wrench, Sparkles, Wallet,
  ArrowLeft, Image as ImageIcon, Video, Volume2, VolumeX, Search, Upload, X,
} from "lucide-react";

const CATEGORIES = [
  { key: "update", label: "Atualização", icon: Sparkles },
  { key: "implementation", label: "Implementação", icon: Wrench },
  { key: "warning", label: "Aviso", icon: AlertTriangle },
  { key: "billing", label: "Cobrança", icon: Wallet },
  { key: "maintenance", label: "Manutenção", icon: Wrench },
  { key: "tip", label: "Dica", icon: Megaphone },
  { key: "welcome", label: "Bem-vindo", icon: Megaphone },
];

const FILTER_CHIPS = [
  { key: "all", label: "Todas as lojas" },
  { key: "active", label: "Ativas" },
  { key: "disabled", label: "Desativadas" },
  { key: "trial", label: "Em teste" },
  { key: "expired", label: "Teste expirado" },
  { key: "exp_5d", label: "Vencendo (5d)" },
  { key: "trial_3d", label: "Trial vencendo (3d)" },
  { key: "no_sub", label: "Sem assinatura" },
];

const STATUS_LABELS = {
  active: { label: "Ativa", color: "bg-emerald-100 text-emerald-800" },
  paused: { label: "Pausada", color: "bg-amber-100 text-amber-800" },
  scheduled: { label: "Agendada", color: "bg-blue-100 text-blue-800" },
  draft: { label: "Rascunho", color: "bg-neutral-200 text-neutral-700" },
};

// Helpers de timezone: o <input type="datetime-local"> retorna "YYYY-MM-DDTHH:MM"
// sem fuso, e o backend interpreta como UTC se vier sem timezone. Precisamos
// converter pro ISO UTC antes de mandar, e o caminho de volta pra exibir.
const localInputToUtcIso = (s) => {
  if (!s) return null;
  try { return new Date(s).toISOString(); } catch { return null; }
};
const utcIsoToLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};

export default function MasterNotifications() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [plans, setPlans] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    const r = await api.get("/master/notifications", { _cache: false });
    setItems(r.data || []);
    try {
      const p = await api.get("/master/plans", { _cache: false });
      setPlans(p.data || []);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter(n => {
      if (filter !== "all" && (n.status || "active") !== filter) return false;
      if (search) {
        const blob = `${n.title} ${n.subtitle || ""} ${n.text || ""}`.toLowerCase();
        if (!blob.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [items, filter, search]);

  const handleAction = async (n, action) => {
    try {
      if (action === "pause") await api.post(`/master/notifications/${n.id}/pause`);
      if (action === "resume") await api.post(`/master/notifications/${n.id}/resume`);
      if (action === "duplicate") { await api.post(`/master/notifications/${n.id}/duplicate`); toast.success("Notificação duplicada"); }
      if (action === "delete") {
        if (!window.confirm(`Excluir a notificação "${n.title}"?`)) return;
        await api.delete(`/master/notifications/${n.id}`);
        toast.success("Notificação excluída");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  return (
    <div className="space-y-6" data-testid="master-notifications-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <button onClick={() => nav("/master")} className="text-xs text-neutral-500 hover:text-black mb-2 inline-flex items-center gap-1">
            <ArrowLeft size={12}/> Voltar
          </button>
          <p className="label-tiny mb-2">Master</p>
          <h1 className="font-display text-4xl tracking-tight">Notificações</h1>
          <p className="text-sm text-neutral-500 mt-1">Avise seus lojistas sobre atualizações, manutenção, cobrança e mais.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="new-notification-button">
          <Plus size={16} className="mr-1.5"/> Nova notificação
        </Button>
      </div>

      <Card className="p-4 border-[#E5E5E5] shadow-none">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
            <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar título ou conteúdo…" className="pl-9" data-testid="notif-search-input"/>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="notif-status-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="active">Ativas</SelectItem>
              <SelectItem value="paused">Pausadas</SelectItem>
              <SelectItem value="scheduled">Agendadas</SelectItem>
              <SelectItem value="draft">Rascunhos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="space-y-3">
        {filtered.map(n => {
          const cat = CATEGORIES.find(c => c.key === n.category) || CATEGORIES[0];
          const status = STATUS_LABELS[n.status] || STATUS_LABELS.active;
          const reads = (n.reads || []).length;
          const confirms = (n.confirmations || []).length;
          const Icon = cat.icon;
          return (
            <Card key={n.id} className="p-4 border-[#E5E5E5] shadow-none" data-testid={`notif-${n.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="h-10 w-10 rounded-md bg-neutral-100 flex items-center justify-center flex-shrink-0">
                  <Icon size={16}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-lg tracking-tight truncate">{n.title}</h3>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${status.color}`}>{status.label}</span>
                    <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded bg-neutral-100 text-neutral-600">{cat.label}</span>
                  </div>
                  {n.subtitle && <p className="text-xs text-neutral-500 mt-0.5">{n.subtitle}</p>}
                  <p className="text-sm text-neutral-600 mt-2 line-clamp-2">{n.text}</p>
                  <div className="text-[11px] text-neutral-500 mt-2 flex flex-wrap gap-3">
                    <span>👁 {reads} visualizações</span>
                    <span>✓ {confirms} confirmadas</span>
                    <span>Filtros: {(n.filters || []).join(", ") || "all"}</span>
                    {n.image_url && <span className="inline-flex items-center gap-1"><ImageIcon size={11}/> Imagem</span>}
                    {n.video_url && <span className="inline-flex items-center gap-1"><Video size={11}/> Vídeo</span>}
                    {n.play_sound ? <span className="inline-flex items-center gap-1"><Volume2 size={11}/> Som</span> : <span className="inline-flex items-center gap-1 text-neutral-400"><VolumeX size={11}/></span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {n.status === "active" && (
                    <Button size="sm" variant="outline" onClick={() => handleAction(n, "pause")} data-testid={`notif-pause-${n.id}`}><Pause size={12} className="mr-1"/>Pausar</Button>
                  )}
                  {n.status === "paused" && (
                    <Button size="sm" variant="outline" onClick={() => handleAction(n, "resume")} data-testid={`notif-resume-${n.id}`}><Play size={12} className="mr-1"/>Retomar</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setEditTarget(n)} data-testid={`notif-edit-${n.id}`}><Edit2 size={12} className="mr-1"/>Editar</Button>
                  <Button size="sm" variant="outline" onClick={() => handleAction(n, "duplicate")} data-testid={`notif-dup-${n.id}`}><Copy size={12} className="mr-1"/>Duplicar</Button>
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleAction(n, "delete")} data-testid={`notif-del-${n.id}`}><Trash2 size={12}/></Button>
                </div>
              </div>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-16 text-sm text-neutral-500">Nenhuma notificação encontrada.</div>
        )}
      </div>

      <NotificationDialog
        open={showCreate || !!editTarget}
        onClose={() => { setShowCreate(false); setEditTarget(null); }}
        target={editTarget}
        plans={plans}
        onSaved={() => { setShowCreate(false); setEditTarget(null); load(); }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Create/Edit Dialog
// ─────────────────────────────────────────────────────────────────────────────
const blank = {
  title: "", subtitle: "", text: "", category: "update",
  image_url: "", video_url: "", play_sound: false,
  filters: ["all"],
  visible_until: "",
  schedule_at: "",
  status: "active",
};

function NotificationDialog({ open, onClose, target, plans, onSaved }) {
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [recipients, setRecipients] = useState({ count: 0, samples: [] });

  useEffect(() => {
    if (target) {
      setForm({
        ...blank,
        ...target,
        filters: target.filters || ["all"],
        // converte ISO UTC do backend para o formato local do input
        visible_until: utcIsoToLocalInput(target.visible_until),
        schedule_at: utcIsoToLocalInput(target.schedule_at),
      });
    } else {
      setForm(blank);
    }
  }, [target, open]);

  // preview de destinatários — atualiza ao mudar filtros
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(async () => {
      try {
        const r = await api.post("/master/notifications/preview-recipients", { filters: form.filters || ["all"] });
        setRecipients(r.data || { count: 0, samples: [] });
      } catch (e) {}
    }, 250);
    return () => clearTimeout(id);
  }, [form.filters, open]);

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleImageFile = (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Imagem deve ter no máximo 3MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => upd("image_url", e.target.result);
    reader.readAsDataURL(file);
  };

  const toggleFilter = (key) => {
    setForm(prev => {
      const cur = prev.filters || [];
      // se clicou "all", limpa os outros
      if (key === "all") return { ...prev, filters: ["all"] };
      const without_all = cur.filter(x => x !== "all");
      const exists = without_all.includes(key);
      const next = exists ? without_all.filter(x => x !== key) : [...without_all, key];
      return { ...prev, filters: next.length ? next : ["all"] };
    });
  };

  const togglePlan = (planName) => {
    const key = `plan:${planName}`;
    setForm(prev => {
      const cur = (prev.filters || []).filter(x => x !== "all");
      const exists = cur.includes(key);
      const next = exists ? cur.filter(x => x !== key) : [...cur, key];
      return { ...prev, filters: next.length ? next : ["all"] };
    });
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Título é obrigatório"); return; }
    if (!form.text.trim()) { toast.error("Texto é obrigatório"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        // converte do local input (sem TZ) para ISO UTC antes de enviar
        visible_until: localInputToUtcIso(form.visible_until),
        schedule_at: localInputToUtcIso(form.schedule_at),
      };
      if (target) {
        await api.put(`/master/notifications/${target.id}`, payload);
        toast.success("Notificação atualizada");
      } else {
        const r = await api.post("/master/notifications", payload);
        const cnt = r.data?.recipients_count;
        toast.success(cnt != null ? `Notificação criada — ${cnt} lojista(s) receberão` : "Notificação criada");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:w-auto sm:max-w-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="notif-dialog">
        <DialogHeader>
          <DialogTitle>{target ? "Editar notificação" : "Nova notificação"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Título *</Label>
              <Input value={form.title} onChange={(e)=>upd("title", e.target.value)} placeholder="Ex.: Novo módulo lançado" data-testid="notif-title-input"/>
            </div>
            <div>
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v)=>upd("category", v)}>
                <SelectTrigger data-testid="notif-category-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Subtítulo (opcional)</Label>
            <Input value={form.subtitle} onChange={(e)=>upd("subtitle", e.target.value)} placeholder="Resumo curto"/>
          </div>

          <div>
            <Label>Mensagem *</Label>
            <Textarea value={form.text} onChange={(e)=>upd("text", e.target.value)} rows={5} placeholder="O conteúdo que o lojista verá no modal" data-testid="notif-text-input"/>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Imagem (opcional)</Label>
              {form.image_url ? (
                <div className="relative">
                  <img src={form.image_url} alt="" className="w-full h-32 object-cover rounded-md border border-[#E5E5E5]"/>
                  <button type="button" onClick={() => upd("image_url", "")}
                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center"
                    title="Remover imagem" data-testid="notif-image-remove">
                    <X size={12}/>
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 w-full h-32 border-2 border-dashed border-[#E5E5E5] rounded-md text-sm text-neutral-500 cursor-pointer hover:bg-neutral-50 transition" data-testid="notif-image-upload-label">
                  <Upload size={16}/> Enviar foto
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => handleImageFile(e.target.files?.[0])}
                    data-testid="notif-image-file-input"/>
                </label>
              )}
              <p className="text-[10px] text-neutral-500 mt-1">JPG/PNG até 3MB.</p>
            </div>
            <div>
              <Label>URL de vídeo YouTube (opcional)</Label>
              <Input value={form.video_url} onChange={(e)=>upd("video_url", e.target.value)} placeholder="https://youtu.be/…"/>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" id="play_sound" checked={!!form.play_sound} onChange={(e)=>upd("play_sound", e.target.checked)} data-testid="notif-sound-checkbox"/>
            <label htmlFor="play_sound" className="text-sm">Tocar som ao exibir</label>
          </div>

          <div>
            <Label>Destinatários (filtros — escolha 1 ou mais)</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {FILTER_CHIPS.map(f => {
                const on = (form.filters || []).includes(f.key);
                return (
                  <button key={f.key} type="button" onClick={() => toggleFilter(f.key)}
                    className={`text-xs px-2.5 py-1 rounded-md border transition ${on ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-neutral-700 border-[#E5E5E5] hover:bg-neutral-50"}`}
                    data-testid={`notif-filter-${f.key}`}>
                    {f.label}
                  </button>
                );
              })}
            </div>
            {plans.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-neutral-500 mb-1">Por plano:</p>
                <div className="flex flex-wrap gap-1.5">
                  {plans.map(p => {
                    const on = (form.filters || []).includes(`plan:${p.name || p.key}`);
                    return (
                      <button key={p.key} type="button" onClick={() => togglePlan(p.name || p.key)}
                        className={`text-xs px-2.5 py-1 rounded-md border transition ${on ? "bg-violet-600 text-white border-violet-600" : "bg-white text-neutral-700 border-[#E5E5E5] hover:bg-neutral-50"}`}>
                        {p.name || p.key}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Agendar para (opcional)</Label>
              <Input type="datetime-local" value={form.schedule_at} onChange={(e)=>upd("schedule_at", e.target.value)}/>
            </div>
            <div>
              <Label>Visível até (opcional)</Label>
              <Input type="datetime-local" value={form.visible_until} onChange={(e)=>upd("visible_until", e.target.value)}/>
            </div>
          </div>

          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v)=>upd("status", v)}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativa (publica agora)</SelectItem>
                <SelectItem value="paused">Pausada</SelectItem>
                <SelectItem value="draft">Rascunho</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 p-3 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-900" data-testid="notif-recipients-preview">
          <p className="font-medium">{recipients.count > 0 ? `${recipients.count} lojista(s) receberão` : "Nenhum lojista corresponde aos filtros atuais"}</p>
          {recipients.samples && recipients.samples.length > 0 && (
            <p className="text-blue-700 mt-1 truncate">Ex.: {recipients.samples.join(" · ")}{recipients.count > recipients.samples.length ? " …" : ""}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="notif-save-button">
            {busy ? "Salvando…" : (target ? "Salvar" : "Criar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
